import { useEffect, useState } from 'react';
import { Pane, ClipboardIcon, Heading, SelectMenu, Button, majorScale, Text, Spinner, toaster, Pagination } from 'evergreen-ui';
import { useSearchParams } from 'react-router-dom';
import DiaryService from '../services/diaryService';
import { getGuestById } from '../services/guestsServices';

const RANGE_OPTIONS = [
  { label: 'Ultimo mese', value: 1 },
  { label: 'Ultimi 3 mesi', value: 3 },
  { label: 'Ultimi 6 mesi', value: 6 },
  { label: 'Ultimo anno', value: 12 },
  { label: 'Ultimi 2 anni', value: 24 },
  { label: 'Tutte le note', value: 0 }
];

const ResearchNotesPage = () => {
  const [searchParams] = useSearchParams();
  const guestIdParam = searchParams.get('guest_id');
  const guestId = guestIdParam ? parseInt(guestIdParam, 10) : null;

  const [months, setMonths] = useState(3);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [guestName, setGuestName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [notes, months]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!guestId) return;
      setLoading(true);
      try {
        const m = months && months > 0 ? months : null;
        const res = await DiaryService.getNotesByGuest(guestId, m);
        if (!mounted) return;
        if (res && res.success) {
          const data = res.data || [];
          setNotes(data);
          try {
            const guestResp = await getGuestById(guestId);
            const g = guestResp && guestResp.data ? guestResp.data : guestResp;
            const name = g ? `${g.nome || ''} ${g.cognome || ''}`.trim() : '';
            if (name) setGuestName(name);
            else if (data.length > 0 && data[0].guest_name) setGuestName(data[0].guest_name);
            else setGuestName('');
          } catch (e) {
            if (data.length > 0 && data[0].guest_name) setGuestName(data[0].guest_name);
            else setGuestName('');
          }
        } else {
          setNotes([]);
          toaster.danger('Errore nel recupero delle note');
        }
      } catch (err) {
        console.error('Errore fetching notes:', err);
        toaster.danger('Errore nella richiesta delle note');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [guestId, months]);

  const formatForCopy = () => {
    if (!notes || notes.length === 0) return '';
    const from = (currentPage - 1) * PAGE_SIZE;
    const pageItems = notes.slice(from, from + PAGE_SIZE);
    const formatTime = (v) => {
      if (!v) return '-';
      if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(String(v))) return String(v);
      const d = new Date(v);
      if (!isNaN(d.getTime())) return d.toLocaleTimeString();
      return String(v);
    };

    const heading = `Ricerca diario per ospite: ${guestName || guestId || ''}`;
    const body = pageItems.map((n) => {
      const action = n.azione || '';
      const start = formatTime(n.start_orario || n.startTime || n.diario_start_time);
      const end = formatTime(n.end_orario || n.endTime || n.diario_end_time);
      const recordedBy = n.recorded_by || '-';
      const date = n.diario_timestamp ? new Date(n.diario_timestamp).toLocaleDateString() : '-';
      return `Nota: ${action}\nOra inizio: ${start}\nOra fine: ${end}\nRegistrato da: ${recordedBy}\nGiorno diario: ${date}\n---`;
    }).join('\n');

    return `${heading}\n\n${body}`;
  };

  const handleCopyPage = async () => {
    try {
      const text = formatForCopy();
      await navigator.clipboard.writeText(text);
      toaster.success('Testo copiato negli appunti (pagina corrente)');
    } catch (err) {
      console.error('Copy failed', err);
      toaster.danger('Copia negli appunti non riuscita');
    }
  };

  const formatAllForCopy = () => {
    if (!notes || notes.length === 0) return '';
    const formatTime = (v) => {
      if (!v) return '-';
      const d = new Date(v);
      if (!isNaN(d.getTime())) return d.toLocaleTimeString();
      return v;
    };

    const heading = `Ricerca diario per ospite: ${guestName || guestId || ''}`;
    const body = notes.map((n) => {
      const action = n.azione || '';
      const start = formatTime(n.start_orario || n.startTime || n.diario_start_time);
      const end = formatTime(n.end_orario || n.endTime || n.diario_end_time);
      const recordedBy = n.recorded_by || '-';
      const date = n.diario_timestamp ? new Date(n.diario_timestamp).toLocaleDateString() : '-';
      return `Nota: ${action}\nOra inizio: ${start}\nOra fine: ${end}\nRegistrato da: ${recordedBy}\nGiorno diario: ${date}\n---`;
    }).join('\n');

    return `${heading}\n\n${body}`;
  };

  const handleCopyAll = async () => {
    try {
      const text = formatAllForCopy();
      await navigator.clipboard.writeText(text);
      toaster.success('Testo copiato negli appunti (tutte le note filtrate)');
    } catch (err) {
      console.error('Copy failed', err);
      toaster.danger('Copia negli appunti non riuscita');
    }
  };

  return (
    <Pane padding={majorScale(4)}>
      <Heading size={700} marginBottom={12}>Ricerca diario per ospite{guestName ? `: ${guestName}` : ''}</Heading>
      {!guestId && <Text>Guest ID mancante. Aprire questa pagina tramite il bottone nella scheda PEI dell'ospite.</Text>}

      {guestId && (
        <>
          <Pane display="flex" gap={12} alignItems="center" flexWrap="wrap" marginBottom={12}>
            <SelectMenu
              title="Filtro periodo"
              options={RANGE_OPTIONS.map(o => ({ label: o.label, value: String(o.value) }))}
              selected={String(months)}
              onSelect={(item) => setMonths(parseInt(item.value, 10))}
            >
              <Button flexShrink={0}>Periodo: {RANGE_OPTIONS.find(o => o.value === months)?.label || 'Custom'}</Button>
            </SelectMenu>

            <Button onClick={handleCopyPage} appearance="primary" intent="success" flexShrink={0}>Copia pagina <ClipboardIcon marginLeft={8} /></Button>
            <Button onClick={handleCopyAll} appearance="primary" intent="success" flexShrink={0}>Copia tutto <ClipboardIcon marginLeft={8} /></Button>
          </Pane>

          {loading ? (
            <Spinner />
          ) : (
            <Pane>
              {notes.length === 0 ? (
                <Text>Nessuna nota trovata per il periodo selezionato.</Text>
              ) : (
                <Pane>
                  {(() => {
                    const from = (currentPage - 1) * PAGE_SIZE;
                    const pageItems = notes.slice(from, from + PAGE_SIZE);

                    const formatTime = (v) => {
                      if (!v) return '-';
                      if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(String(v))) return String(v);
                      const d = new Date(v);
                      if (!isNaN(d.getTime())) return d.toLocaleTimeString();
                      return String(v);
                    };

                    return pageItems.map((n) => {
                      const action = n.azione || '';
                      const start = formatTime(n.start_orario || n.startTime || n.diario_start_time);
                      const end = formatTime(n.end_orario || n.endTime || n.diario_end_time);
                      const recordedBy = n.recorded_by || '-';
                      const date = n.diario_timestamp ? new Date(n.diario_timestamp).toLocaleDateString() : '-';
                      return (
                        <Pane key={n.note_id} background="tint2" padding={12} borderRadius={6} marginBottom={8}>
                          <Text whiteSpace="pre-wrap">{`Nota: ${action}\nOra inizio: ${start}\nOra fine: ${end}\nRegistrato da: ${recordedBy}\nGiorno diario: ${date}`}</Text>
                        </Pane>
                      );
                    });
                  })()}

                  <Pane display="flex" alignItems="center" justifyContent="center" marginTop={12}>
                      <Pagination
                        totalPages={Math.max(1, Math.ceil(notes.length / PAGE_SIZE))}
                        page={currentPage}
                        onPageChange={(page) => setCurrentPage(page)}
                        onNextPage={() => setCurrentPage((prev) => Math.min(Math.max(1, Math.ceil(notes.length / PAGE_SIZE)), prev + 1))}
                        onPreviousPage={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      />
                  </Pane>
                </Pane>
              )}
            </Pane>
          )}
        </>
      )}
    </Pane>
  );
};

export default ResearchNotesPage;
